import { useRef } from "react";
import { coverImageStyle, type BookSummary } from "@greg-chick/shared";

/**
 * 🖼️ **표지에서 보여줄 곳 정하기** — 고르기 화면 카드와 **같은 모양**(3:4) 위에서 끌어 옮긴다.
 *
 * ## 왜 필요한가 (2026-08-21 신고)
 * 카드는 3:4 인데 표지 그림은 비율이 제각각이다. 꽉 채우면 넘치는 쪽이 잘리는데,
 * 하필 **제목이 잘렸다.** 아이는 그 카드로 책을 알아보는데 제목이 반만 보였다.
 *
 * ## 미리보기가 진짜여야 한다
 * 여기 보이는 것과 아이 화면이 다르면 이 화면은 쓸모가 없다. 그래서 **같은 함수**
 * (`coverImageStyle`)와 **같은 비율**을 쓴다 — 값을 여기서 다시 계산하지 않는다.
 */
export function CoverFramePicker({
  url,
  fit,
  pos,
  onChange,
}: {
  url: string;
  fit: BookSummary["coverFit"];
  pos: BookSummary["coverPos"];
  onChange: (patch: { coverFit?: "cover" | "contain"; coverPos?: { x: number; y: number } }) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  /**
   * 🔴 끄는 중인지를 **state 가 아니라 ref** 로 둔다.
   *
   * state 로 두면 `pointerdown` 에서 켠 값이 **다음 `pointermove` 가 읽을 때까지 반영되지 않는다**
   * (같은 처리 묶음 안에서는 옛 값을 본다). 실제로 끌어도 아무 일이 없었다.
   * ref 는 즉시 바뀌므로 그런 틈이 없다.
   */
  const dragging = useRef(false);
  const style = coverImageStyle({ coverFit: fit, coverPos: pos });
  const contain = fit === "contain";

  function moveTo(clientX: number, clientY: number) {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    const clamp = (v: number) => Math.min(1, Math.max(0, v));
    onChange({
      coverPos: { x: clamp((clientX - r.left) / r.width), y: clamp((clientY - r.top) / r.height) },
    });
  }

  return (
    <div className="coverfit">
      <p className="hint">
        아이가 보는 <b>책 카드 모양</b>이에요. 잘리는 게 싫으면 <b>[전체 보이기]</b>,
        보여줄 곳을 고르려면 그림을 <b>끌어서</b> 옮기세요.
      </p>

      <div className="coverfit__row">
        {/* 누른 자리로 **바로** 옮긴다 — 끌지 않고 톡 눌러도 되고, 이어서 끌 수도 있다.
            포인터 캡처를 잡아 카드 밖으로 나가도 계속 따라온다(가장자리를 고를 수 있게). */}
        <div
          ref={ref}
          className="coverfit__card"
          onPointerDown={(e) => {
            if (contain) return; // 전체가 보이는 중이면 옮길 게 없다
            e.preventDefault();
            dragging.current = true;
            e.currentTarget.setPointerCapture(e.pointerId);
            moveTo(e.clientX, e.clientY);
          }}
          onPointerMove={(e) => dragging.current && moveTo(e.clientX, e.clientY)}
          onPointerUp={(e) => {
            dragging.current = false;
            e.currentTarget.releasePointerCapture(e.pointerId);
          }}
          onPointerCancel={() => {
            dragging.current = false;
          }}
        >
          <img src={url} alt="" draggable={false} style={style} />
          {/* 책등·책배 — 실제 카드에 있는 것들이라 여기서도 같은 자리를 가린다 */}
          <span className="coverfit__spine" aria-hidden="true" />
          <span className="coverfit__pages" aria-hidden="true">3쪽</span>
        </div>

        <div className="coverfit__tools">
          <div className="seg" role="group" aria-label="표지 담는 방식">
            <button
              type="button"
              className="seg__btn"
              aria-pressed={!contain}
              onClick={() => onChange({ coverFit: "cover" })}
            >
              꽉 채우기
            </button>
            <button
              type="button"
              className="seg__btn"
              aria-pressed={contain}
              onClick={() => onChange({ coverFit: "contain" })}
            >
              전체 보이기
            </button>
          </div>
          <p className="hint">
            {contain
              ? "그림 전체가 보여요. 대신 위아래(또는 좌우)에 여백이 생겨요."
              : "카드를 꽉 채워요. 넘치는 쪽은 잘려요 — 제목이 잘리면 끌어서 옮기거나 [전체 보이기]를 쓰세요."}
          </p>
          {!contain ? (
            <>
              <div className="coverfit__quick">
                <button type="button" onClick={() => onChange({ coverPos: { x: 0.5, y: 0 } })}>위쪽</button>
                <button type="button" onClick={() => onChange({ coverPos: { x: 0.5, y: 0.5 } })}>가운데</button>
                <button type="button" onClick={() => onChange({ coverPos: { x: 0.5, y: 1 } })}>아래쪽</button>
              </div>
              <p className="hint">
                지금 {Math.round((pos?.x ?? 0.5) * 100)}% · {Math.round((pos?.y ?? 0.5) * 100)}%
              </p>
            </>
          ) : null}
          <button type="button" className="btn btn--sm" onClick={() => onChange({ coverFit: "cover", coverPos: undefined })}>
            처음으로 되돌리기
          </button>
        </div>
      </div>
    </div>
  );
}
